import { cn } from '@recipes/ui';
import type { ReactNode } from 'react';

export type IllustrationName =
  | 'recipes'
  | 'shopping'
  | 'mealPlan'
  | 'pantry'
  | 'tasting'
  | 'households'
  | 'notifications'
  | 'offline';

/**
 * One small picture per empty page (docs/partial/appearance-and-themes-spec.md 6.4): drawn with
 * the category tokens, so they follow theme and mode. Decorative – the heading next to it says
 * what is empty. Own work.
 */
export function Illustration({ name, className }: { name: IllustrationName; className?: string }) {
  return (
    <svg
      viewBox="0 0 160 120"
      className={cn('h-28 w-auto', className)}
      aria-hidden
      focusable={false}
      data-testid={`illustration-${name}`}
    >
      {/* the ground every picture stands on */}
      <ellipse cx="80" cy="106" rx="62" ry="8" className="fill-muted" />
      {PICTURES[name]}
    </svg>
  );
}

const PICTURES: Record<IllustrationName, ReactNode> = {
  // An open cookbook with a bookmark and a wooden spoon.
  recipes: (
    <>
      <path d="M22 34q29-12 58 0v62q-29-12-58 0z" className="fill-cat-7-surface" />
      <path d="M138 34q-29-12-58 0v62q29-12 58 0z" className="fill-cat-7-surface" />
      <path d="M80 34v62" className="stroke-cat-7" strokeWidth="3" />
      <g className="stroke-cat-7" strokeWidth="3" strokeLinecap="round">
        <path d="M32 46q19-6 38 0M32 58q19-6 38 0M32 70q19-6 38 0" />
        <path d="M90 46q19-6 38 0M90 58q19-6 38 0" />
      </g>
      <path d="M112 28v30l7-6 7 6V28z" className="fill-cat-2" />
      <g transform="rotate(28 120 86)">
        <rect x="116" y="60" width="7" height="44" rx="3.5" className="fill-cat-4" />
        <ellipse cx="119.5" cy="54" rx="9" ry="13" className="fill-cat-4" />
      </g>
    </>
  ),
  // A shopping bag with a leek, a carrot and a baguette.
  shopping: (
    <>
      <path d="M100 14l14 40" className="stroke-cat-4" strokeWidth="9" strokeLinecap="round" />
      <path d="M58 20l-6 34" className="stroke-cat-1" strokeWidth="8" strokeLinecap="round" />
      <path
        d="M58 20l-10-12M58 20l2-14M58 20l12-9"
        className="stroke-cat-1"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path d="M78 26l4 28" className="stroke-cat-7" strokeWidth="8" strokeLinecap="round" />
      <path
        d="M78 26l-5-10M78 26l5-10"
        className="stroke-cat-1"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M38 50h84l-8 54H46z" className="fill-cat-10" />
      <path d="M38 50h84l-2 12H40z" className="fill-cat-10-foreground opacity-30" />
      <path
        d="M62 66q18 16 36 0"
        className="stroke-cat-10-surface"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
    </>
  ),
  // A week on the wall: a calendar with a few filled days.
  mealPlan: (
    <>
      <rect x="30" y="24" width="100" height="78" rx="8" className="fill-cat-3-surface" />
      <rect x="30" y="24" width="100" height="20" rx="8" className="fill-cat-3" />
      <rect x="48" y="16" width="6" height="16" rx="3" className="fill-cat-3-foreground" />
      <rect x="106" y="16" width="6" height="16" rx="3" className="fill-cat-3-foreground" />
      <g>
        <rect x="40" y="52" width="16" height="14" rx="3" className="fill-cat-6" />
        <rect x="62" y="52" width="16" height="14" rx="3" className="fill-card" />
        <rect x="84" y="52" width="16" height="14" rx="3" className="fill-cat-1" />
        <rect x="106" y="52" width="16" height="14" rx="3" className="fill-card" />
        <rect x="40" y="74" width="16" height="14" rx="3" className="fill-card" />
        <rect x="62" y="74" width="16" height="14" rx="3" className="fill-cat-10" />
        <rect x="84" y="74" width="16" height="14" rx="3" className="fill-card" />
        <rect x="106" y="74" width="16" height="14" rx="3" className="fill-cat-7" />
      </g>
    </>
  ),
  // A shelf with jars.
  pantry: (
    <>
      <rect x="20" y="96" width="120" height="7" rx="3" className="fill-cat-4" />
      <g>
        <rect x="30" y="50" width="28" height="46" rx="6" className="fill-cat-6-surface" />
        <rect x="30" y="72" width="28" height="24" rx="6" className="fill-cat-6" />
        <rect x="33" y="42" width="22" height="10" rx="3" className="fill-cat-4-foreground" />
      </g>
      <g>
        <rect x="66" y="38" width="30" height="58" rx="6" className="fill-cat-2-surface" />
        <rect x="66" y="58" width="30" height="38" rx="6" className="fill-cat-2" />
        <rect x="69" y="30" width="24" height="10" rx="3" className="fill-cat-4-foreground" />
      </g>
      <g>
        <rect x="104" y="56" width="26" height="40" rx="6" className="fill-cat-1-surface" />
        <rect x="104" y="80" width="26" height="16" rx="6" className="fill-cat-1" />
        <rect x="107" y="48" width="20" height="10" rx="3" className="fill-cat-4-foreground" />
      </g>
    </>
  ),
  // A plate with a map pin: eaten somewhere.
  tasting: (
    <>
      <ellipse cx="72" cy="86" rx="46" ry="14" className="fill-cat-5-surface" />
      <ellipse cx="72" cy="84" rx="30" ry="8" className="fill-card" />
      <path
        d="M22 60v32M16 60v12q0 6 6 6t6-6V60"
        className="stroke-cat-5"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M112 22c-12 0-21 9-21 20 0 15 21 36 21 36s21-21 21-36c0-11-9-20-21-20z"
        className="fill-cat-2"
      />
      <circle cx="112" cy="42" r="8" className="fill-cat-2-surface" />
    </>
  ),
  // A house with three people in front of it.
  households: (
    <>
      <path d="M36 58l44-34 44 34v42H36z" className="fill-cat-9-surface" />
      <path
        d="M28 60l52-40 52 40"
        className="stroke-cat-9"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <g>
        <circle cx="58" cy="70" r="7" className="fill-cat-7" />
        <path d="M46 100q0-18 12-18t12 18z" className="fill-cat-7" />
        <circle cx="102" cy="70" r="7" className="fill-cat-8" />
        <path d="M90 100q0-18 12-18t12 18z" className="fill-cat-8" />
        <circle cx="80" cy="76" r="6" className="fill-cat-10" />
        <path d="M70 100q0-15 10-15t10 15z" className="fill-cat-10" />
      </g>
    </>
  ),
  // A bell that has nothing to say.
  notifications: (
    <>
      <path
        d="M80 22c-18 0-28 14-28 32v18l-10 14h76l-10-14V54c0-18-10-32-28-32z"
        className="fill-cat-6"
      />
      <circle cx="80" cy="18" r="5" className="fill-cat-6-foreground" />
      <path d="M70 92q10 12 20 0z" className="fill-cat-6-foreground" />
      <path
        d="M124 30q6 8 6 18M134 24q9 12 9 26M36 30q-6 8-6 18M26 24q-9 12-9 26"
        className="stroke-cat-6 opacity-50"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </>
  ),
  // A cloud with a line through it – and the recipes that are on the device anyway.
  offline: (
    <>
      <path
        d="M46 78a20 20 0 0 1 4-40 26 26 0 0 1 50-4 22 22 0 0 1 12 44z"
        className="fill-cat-5-surface"
      />
      <path d="M40 30l78 60" className="stroke-cat-5" strokeWidth="6" strokeLinecap="round" />
      <rect x="64" y="70" width="34" height="30" rx="4" className="fill-cat-1" />
      <path
        d="M72 80h18M72 88h12"
        className="stroke-cat-1-surface"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </>
  ),
};
